/**
 * CRM plan picker (owner decision 25 Sep 2026): the CRM needs a paid plan. Customer prices only, from the public
 * catalogue, charged in AED or USD. Enterprise is contact sales (no price, no checkout). Partner/wholesale prices are
 * never shown here. Checkout goes through Stripe; access starts when the verified webhook confirms payment.
 */
import { useState } from 'react';
import type { Currency, Money } from '@qm/contracts/billing';
import { Button, Field, Heading, Panel, Select, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';

export interface CatalogueEntry { sku: string; kind: string; name: string; price: Money; interval: string | null; chargePrices?: Money[] }

/** Subscription plans with their price in the chosen currency (base price when the plan is not sold in it). */
export function plansIn(items: readonly CatalogueEntry[], currency: Currency) {
  return items.filter((i) => i.kind === 'subscription').map((i) => ({
    ...i, charge: (i.chargePrices ?? [i.price]).find((p) => p.currency === currency) ?? i.price,
  }));
}

const key = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `idem-${Date.now()}`);

export function PlanPicker() {
  const { t } = useApp();
  const f = useFormat();
  const catalogue = useResource<CatalogueEntry[]>('/v1/billing/catalogue');
  const [currency, setCurrency] = useState<Currency>('AED');
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // one key per picker instance: a double click cannot open two checkouts
  const [idem] = useState(key);

  async function subscribe(planSku: string) {
    setBusy(planSku);
    setFailed(false);
    try {
      const res = await bff<{ checkoutUrl: string }>('/v1/billing/subscription/checkout', {
        method: 'POST', headers: { 'idempotency-key': `${idem}:${planSku}:${currency}` }, body: JSON.stringify({ planSku, currency }),
      });
      window.location.assign(res.checkoutUrl);
    } catch {
      setFailed(true);
      setBusy(null);
    }
  }

  const plans = catalogue.status === 'ready' ? plansIn(catalogue.data ?? [], currency) : [];
  return (
    <section aria-labelledby="plans-heading" data-testid="plan-picker">
      <Heading level={2} id="plans-heading">{t('shell.screens.billing.plans.title')}</Heading>
      <Text tone="muted">{t('shell.screens.billing.plans.intro')}</Text>
      <Field label={t('shell.screens.billing.plans.currency')}>
        {({ id }) => (
          <Select id={id} value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
            <option value="AED">AED</option>
            <option value="USD">USD</option>
          </Select>
        )}
      </Field>
      {plans.map((p) => (
        <Panel key={p.sku} data-plan={p.sku}>
          <Heading level={3}>{p.name}</Heading>
          <Text>{t('shell.screens.billing.plans.perMonth', { price: f.money(p.charge.amountMinor, p.charge.currency) })}</Text>
          <Button variant="primary" disabled={busy !== null} onClick={() => void subscribe(p.sku)}>{t('shell.screens.billing.plans.choose', { plan: p.name })}</Button>
        </Panel>
      ))}
      <Panel data-plan="plan_enterprise">
        <Heading level={3}>{t('shell.screens.billing.plans.enterprise')}</Heading>
        <Text>{t('shell.screens.billing.plans.enterpriseNote')}</Text>
        <a href="mailto:sales@quixomarketing.com">{t('shell.screens.billing.plans.contactSales')}</a>
      </Panel>
      {failed ? <Text role="alert">{t('shell.screens.billing.plans.error')}</Text> : null}
    </section>
  );
}
